import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { isEqual, startOfDay } from 'date-fns'
import { DateCards } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/DateCards/DateCards'
import { useShouldShowLimitedEvent } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/hooks/useShouldShowLimitedEvent'
import {
  generateDates,
  getDateAfterDays,
} from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/utils/utils'

export interface CalendarProps {
  disabled: boolean
  onChange: (date: Date) => void
  selectedDate?: Date
  startDate: Date
  scrollToTop: () => void
  calendarRef: React.RefObject<HTMLDivElement>
  releaseDate: string
  theatricalSlug: string
  className?: string
  specialDate?: Date
}

export const Calendar: React.FC<CalendarProps> = ({
  disabled,
  onChange,
  selectedDate,
  startDate,
  scrollToTop,
  calendarRef,
  releaseDate,
  theatricalSlug,
  className,
  specialDate,
}) => {
  const [selected, setSelected] = useState<number | null>(null)
  const [hasSetInitialDate, setHasSetInitialDate] = useState(false)

  const { shouldShow: shouldShowLimitedEvent, eventDate: limitedEventDate } = useShouldShowLimitedEvent(theatricalSlug)

  const releaseDateObj = new Date(releaseDate)

  const dates: Date[] = useMemo(() => {
    const generatedDates = generateDates(startDate, getDateAfterDays(startDate))
    const today = startOfDay(new Date())
    const futureDates = generatedDates.filter((date) => startOfDay(date) >= today)

    return [
      ...(shouldShowLimitedEvent && limitedEventDate ? [limitedEventDate] : []),
      ...(specialDate ? [specialDate] : []),
      ...futureDates,
    ]
  }, [startDate, shouldShowLimitedEvent, limitedEventDate, specialDate])

  useEffect(() => {
    if (!disabled && selectedDate) {
      const selectedIndex = dates.findIndex((d) => isEqual(startOfDay(d), startOfDay(selectedDate)))
      setSelected(selectedIndex >= 0 ? selectedIndex : null)
    }
  }, [dates, selectedDate, disabled])

  return (
    <div ref={calendarRef} className={classNames(className)}>
      <DateCards
        dates={dates}
        disabled={disabled}
        selected={selected}
        releaseDate={releaseDateObj}
        specialDate={specialDate}
        hasSetInitialDate={hasSetInitialDate}
        theatricalSlug={theatricalSlug}
        setHasSetInitialDate={setHasSetInitialDate}
        onChange={onChange}
        scrollToTop={scrollToTop}
        setSelected={setSelected}
      />
    </div>
  )
}
